import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { Button, Autocomplete } from '@anyemp/ui-kit'
import { useSidebarNav } from '../context/SidebarNavContext'
import FavoriteStar from '../components/Favorites/FavoriteStar'
import {
  ArrowLeft,
  Edit,
  Trash2,
  AlertCircle,
  X as XIcon,
  Merge,
  ArrowRightLeft,
  MoreHorizontal,
} from 'lucide-react'
import { talents, statuses, stages } from '../services/apiService'
import toast from 'react-hot-toast'
import ConfirmDialog from '../components/UI/ConfirmDialog'
import { useAuth } from '../hooks/useAuth'
import { getTalentTypeLabel, getTalentTypeIcon, getTalentTypeHexColor } from '../utils/talentTypeUtils'
import StatusTransitionModal from '../components/StatusManagement/StatusTransitionModal'
import StageTransitionModal from '../components/StageManagement/StageTransitionModal'
import Avatar from '../components/UI/Avatar'
import LazySection from '../components/UI/LazySection'
import MergeTalentModal from '../components/Talent/MergeTalentModal'
import TalentTypeChangeModal from '../components/Talent/TalentTypeChangeModal'
import MobileTalentStickyContext from '../components/Talent/MobileTalentStickyContext'
import { TALENT_DETAIL_SECTIONS as SECTIONS } from '../components/Layout/talentDetailNavigationConfig'
import { filterTalentDetailSections } from '../utils/talentNavigationDomain'
import {
  isTalentDetailLoadedForRoute,
  resolveTalentDetailBackTarget,
  resolveTalentDetailEntryTarget,
  scrollToTalentDetailSection,
} from '../utils/talentDetailSectionNavigation'
import {
  isElementIntersectingViewport,
  shouldShowCompactTalentSummary,
} from '../utils/talentDetailStickyVisibility'

// Section components
import OverviewSection from '../components/Talent/OverviewSection'
import ContactsSection from '../components/Talent/ContactsSection'
import ProfessionsSection from '../components/Talent/ProfessionsSection'
import ToolsSection from '../components/Talent/ToolsSection'
import ResponsibilitiesSection from '../components/Talent/ResponsibilitiesSection'
import LanguagesSection from '../components/Talent/LanguagesSection'
import CVsSection from '../components/Talent/CVsSection'
import MediaSection from '../components/Talent/MediaSection'
import NotesSection from '../components/Talent/NotesSection'
import SalarySection from '../components/Talent/SalarySection'
import ImportantDatesSection from '../components/Talent/ImportantDatesSection'
import StagesSection from '../components/Talent/StagesSection'
import CommunicationsSection from '../components/Talent/CommunicationsSection'
import AISettingsSection from '../components/Talent/AISettingsSection'
import PostsSection from '../components/Talent/PostsSection'
import CoursesSection from '../components/Talent/CoursesSection'
import TasksSection from '../components/Talent/TasksSection'
import AccountsSection from '../components/Talent/AccountsSection'
import HistorySection from '../components/Talent/HistorySection'
import PublicProfileSection from '../components/Talent/PublicProfileSection'

// ─── Scroll section observer ────────────────────────────────────────────────
const useActiveSectionObserver = (sectionIds) => {
  const [activeSection, setActiveSection] = useState(sectionIds[0])
  const isManualScrolling = useRef(false)
  const manualScrollTimeout = useRef(null)

  const setActiveSectionManually = useCallback((sectionId) => {
    setActiveSection(sectionId)
    isManualScrolling.current = true
    if (manualScrollTimeout.current) clearTimeout(manualScrollTimeout.current)
    manualScrollTimeout.current = setTimeout(() => { isManualScrolling.current = false }, 1000)
  }, [])

  useEffect(() => {
    if (!sectionIds.includes(activeSection)) setActiveSection(sectionIds[0])
  }, [activeSection, sectionIds])

  useEffect(() => () => {
    if (manualScrollTimeout.current) clearTimeout(manualScrollTimeout.current)
  }, [])

  useEffect(() => {
    const updateActiveSection = () => {
      if (isManualScrolling.current) return
      let closestSection = null
      let closestDistance = Infinity
      sectionIds.forEach(id => {
        const el = document.getElementById(id)
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          const distance = Math.abs(rect.top)
          if (distance < closestDistance) { closestDistance = distance; closestSection = id }
        }
      })
      if (closestSection) setActiveSection(closestSection)
    }
    const handleScroll = () => { requestAnimationFrame(updateActiveSection) }
    updateActiveSection()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sectionIds.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  return [activeSection, setActiveSectionManually]
}


const SecondaryActionsMenu = ({ onMerge, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setIsOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div ref={menuRef} className="relative lg:hidden">
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-light-border-primary bg-light-bg-primary text-light-text-secondary transition-colors hover:bg-light-bg-tertiary hover:text-light-text-primary dark:border-dark-border-primary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-bg-primary dark:hover:text-dark-text-primary"
        aria-label="More talent actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-2 w-44 overflow-hidden rounded-lg border border-light-border-primary bg-light-bg-primary py-1 shadow-lg dark:border-dark-border-primary dark:bg-dark-bg-secondary"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setIsOpen(false); onMerge() }}
            className="flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-700 transition-colors hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
          >
            <Merge className="h-4 w-4" />
            Merge duplicate
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setIsOpen(false); onDelete() }}
            className="flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            Delete talent
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────
const TalentDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getToken, hasPermission } = useAuth()

  const [talent, setTalent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Status editing
  const [isEditingStatus, setIsEditingStatus] = useState(false)
  const [statusOptions, setStatusOptions] = useState([])
  const [selectedStatusId, setSelectedStatusId] = useState(null)
  const [statusTransitionOpen, setStatusTransitionOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState(null)

  // Stage — a separate axis from Status ("where the talent is" vs "what's
  // going on with them there"); same edit/transition-modal flow as Status.
  const [isEditingStage, setIsEditingStage] = useState(false)
  const [stageOptions, setStageOptions] = useState([])
  const [selectedStageId, setSelectedStageId] = useState(null)
  const [stageTransitionOpen, setStageTransitionOpen] = useState(false)
  const [pendingStage, setPendingStage] = useState(null)

  // Смена типа таланта — только через PATCH /talents/:id/type
  const [typeChangeOpen, setTypeChangeOpen] = useState(false)

  // Объединение дубликата в этот профиль
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // ── Visible sections ────────────────────────────────────────────────────────
  const visibleSections = useMemo(() => talent
    ? filterTalentDetailSections({
        sections: SECTIONS,
        talentType: talent.talent_type,
        hasPermission,
      })
    : [], [talent, hasPermission]) // eslint-disable-line react-hooks/exhaustive-deps

  const visibleSectionIds = useMemo(() => visibleSections.map(s => s.id), [visibleSections])

  // ── Active section tracking ─────────────────────────────────────────────────
  const [activeSection, setActiveSectionManually] = useActiveSectionObserver(visibleSectionIds)

  const scrollToSection = useCallback((sectionId) => {
    const didScroll = scrollToTalentDetailSection({ sectionId, visibleSectionIds })
    if (didScroll) setActiveSectionManually(sectionId)
    return didScroll
  }, [setActiveSectionManually, visibleSectionIds])

  // ── Sync section nav into Layout sidebar ────────────────────────────────────
  const { setSidebarNav, setBackPath } = useSidebarNav()
  const location = useLocation()
  const backTo = resolveTalentDetailBackTarget(location.state?.from)
  const processedHashRef = useRef(null)
  const heroSummaryRef = useRef(null)
  const [isHeroVisible, setIsHeroVisible] = useState(true)

  useEffect(() => {
    setBackPath(backTo)
    return () => setBackPath(null)
  }, [backTo, setBackPath])

  useEffect(() => {
    if (!talent || loading || error || visibleSections.length === 0) {
      setSidebarNav(null)
      return
    }
    setSidebarNav({ sections: visibleSections, activeId: activeSection, onItemClick: scrollToSection })
  }, [visibleSections, activeSection, scrollToSection, talent, loading, error, setSidebarNav])

  useEffect(() => {
    if (!isTalentDetailLoadedForRoute(talent, id) || loading || error || visibleSectionIds.length === 0) return

    const processingKey = `${id}:${location.hash}:${visibleSectionIds.join(',')}`
    if (processedHashRef.current === processingKey) return
    processedHashRef.current = processingKey

    const entryTarget = resolveTalentDetailEntryTarget(location.hash, visibleSectionIds)
    if (entryTarget.type === 'section') {
      scrollToSection(entryTarget.sectionId)
      return
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    const fallbackSectionId = visibleSectionIds.includes('overview')
      ? 'overview'
      : visibleSectionIds[0]
    setActiveSectionManually(fallbackSectionId)
  }, [
    error,
    id,
    loading,
    location.hash,
    scrollToSection,
    setActiveSectionManually,
    talent,
    visibleSectionIds,
  ])

  useEffect(() => {
    return () => setSidebarNav(null)
  }, [setSidebarNav])

  useLayoutEffect(() => {
    if (!isTalentDetailLoadedForRoute(talent, id) || loading) {
      setIsHeroVisible(true)
      return undefined
    }

    const heroSummary = heroSummaryRef.current
    if (!heroSummary) return undefined

    let disposed = false
    const updateFromRect = () => {
      if (disposed || heroSummaryRef.current !== heroSummary) return
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      setIsHeroVisible(isElementIntersectingViewport(heroSummary.getBoundingClientRect(), viewportHeight))
    }

    updateFromRect()

    if (typeof IntersectionObserver === 'undefined') {
      window.addEventListener('scroll', updateFromRect, { passive: true })
      window.addEventListener('resize', updateFromRect)
      return () => {
        disposed = true
        window.removeEventListener('scroll', updateFromRect)
        window.removeEventListener('resize', updateFromRect)
      }
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (disposed || heroSummaryRef.current !== heroSummary) return
      setIsHeroVisible(entry.isIntersecting)
    }, { threshold: 0.01 })

    observer.observe(heroSummary)
    return () => {
      disposed = true
      observer.disconnect()
    }
  }, [id, talent?.id, loading])

  // ── Load talent ─────────────────────────────────────────────────────────────
  const fetchTalent = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await talents.getTalentByIdWithRelations(id)
      if (response && response.data) {
        setTalent(response.data)
      } else {
        throw new Error('Invalid response structure')
      }
    } catch (err) {
      console.error('Error fetching talent:', err)
      if (err.response?.status === 403) {
        setError('Access denied')
      } else {
        setError('Error loading talent data')
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (id) fetchTalent()
  }, [id, fetchTalent])

  // ── Status handlers ─────────────────────────────────────────────────────────
  const handleEditStatus = async () => {
    setSelectedStatusId(talent.currentStatus?.id ? String(talent.currentStatus.id) : null)
    try {
      if (talent.currentStage?.id) {
        // Статусы, реально допустимые на текущей стадии — не весь список по
        // типу таланта. Порядок — display_order связки: первый элемент это
        // дефолт стадии, ломать его алфавитной пересортировкой не стоит.
        const res = await stages.getStatusesForStage(talent.currentStage.id)
        const list = res.data || res || []
        setStatusOptions(list.map(s => ({ ...s, value: String(s.id), label: s.name })))
      } else {
        // Стадии у таланта нет (не должно случаться — current_stage_id NOT NULL
        // в БД, но на всякий случай) — как раньше, весь список по типу
        const res = await statuses.getStatusesByType(talent.talent_type)
        const list = res.data || res || []
        setStatusOptions(
          list
            .map(s => ({ ...s, value: String(s.id), label: s.name }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        )
      }
    } catch (err) {
      console.error('Error loading statuses:', err)
      setStatusOptions([])
    }
    setIsEditingStatus(true)
  }

  const handleStatusTransitionSuccess = async () => {
    setStatusTransitionOpen(false)
    setIsEditingStatus(false)
    const response = await talents.getTalentByIdWithRelations(id)
    setTalent(response.data)
  }

  // ── Stage handlers ──────────────────────────────────────────────────────────
  const handleEditStage = async () => {
    setSelectedStageId(talent.currentStage?.id ? String(talent.currentStage.id) : null)
    try {
      const res = await stages.getStages()
      const list = res.data || res || []
      setStageOptions(
        list
          .map(s => ({ ...s, value: String(s.id), label: s.name }))
          .sort((a, b) => (a.stage_order || 0) - (b.stage_order || 0))
      )
    } catch (err) {
      console.error('Error loading stages:', err)
      setStageOptions([])
    }
    setIsEditingStage(true)
  }

  const handleStageTransitionSuccess = async () => {
    setStageTransitionOpen(false)
    setIsEditingStage(false)
    const response = await talents.getTalentByIdWithRelations(id)
    setTalent(response.data)
  }

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true)
    try {
      await talents.deleteTalent(id)
      navigate(backTo, { replace: true })
    } catch (err) {
      console.error('Error deleting talent:', err)
      toast.error('Error deleting talent')
      setDeleting(false)
    }
  }

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="bg-light-bg-secondary dark:bg-dark-bg-primary">
          <div className="overflow-hidden rounded-xl border border-light-border-primary bg-light-bg-primary shadow-sm dark:border-dark-border-primary dark:bg-dark-bg-secondary">
            <div className="h-0.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-400" />
            <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:px-5 lg:py-2.5">
              <div className="flex min-w-0 items-start gap-3 lg:items-center lg:gap-3.5">
                <div className="h-10 w-10 shrink-0 rounded-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary lg:h-12 lg:w-12" />
                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 h-5 w-3/4 max-w-56 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
                  <div className="mb-2 flex items-center gap-2 lg:hidden">
                    <div className="h-5 w-20 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
                    <div className="h-5 w-24 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="h-5 w-20 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
                    <div className="h-5 w-24 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
                    <div className="h-5 w-24 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
                <div className="h-10 w-24 rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary lg:h-8 lg:w-20" />
                <div className="h-10 w-10 rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary lg:h-8 lg:w-8" />
                <div className="h-10 w-10 rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary lg:hidden" />
              </div>
            </div>
          </div>
        </div>
        <div className="sticky top-0 z-20 -mx-1 bg-light-bg-secondary px-1 py-2 dark:bg-dark-bg-primary min-[992px]:hidden">
          <div className="flex gap-1.5 overflow-hidden">
            {[72, 88, 80, 96].map(width => (
              <div key={width} className="h-8 shrink-0 rounded-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary" style={{ width }} />
            ))}
          </div>
        </div>
        <div className="mt-4 space-y-4">
          <div className="bg-light-bg-primary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-xl p-5">
            <div className="h-5 w-24 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded mb-4" />
            <div className="grid grid-cols-2 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                  <div className="h-3 w-16 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded mb-2" />
                  <div className="h-4 w-28 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded" />
                </div>
              ))}
            </div>
          </div>
          <div className="bg-light-bg-primary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-xl p-5">
            <div className="h-5 w-20 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded mb-4" />
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded bg-light-bg-secondary dark:bg-dark-bg-secondary" />
                    <div className="space-y-1.5">
                      <div className="h-3.5 w-24 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded" />
                      <div className="h-3 w-36 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-light-bg-primary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-xl p-5">
            <div className="h-5 w-28 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded mb-4" />
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                  <div className="h-4 w-40 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded mb-2" />
                  <div className="h-3 w-24 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Error state ─────────────────────────────────────────────────────────────
  if (error || !talent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4 min-[992px]:hidden">
          <Link to={backTo} className="inline-flex items-center px-3 py-2 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-lg text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to list
          </Link>
        </div>
        <div className="bg-light-bg-primary dark:bg-dark-bg-secondary shadow rounded-lg p-12 text-center border border-light-border-primary dark:border-dark-border-primary">
          <div className="mx-auto h-24 w-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-12 w-12 text-red-400 dark:text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-red-900 dark:text-red-400 mb-2">
            {error === 'Access denied' ? 'Access Denied' : 'Loading Error'}
          </h3>
          <p className="text-red-500 dark:text-red-400 mb-6">{error || 'Talent not found'}</p>
          <Link to={backTo} className="inline-flex items-center px-4 py-2 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-lg text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
            To talents list
          </Link>
        </div>
      </div>
    )
  }

  const TypeIcon = getTalentTypeIcon(talent.talent_type)
  const showCompactSummary = shouldShowCompactTalentSummary(isHeroVisible)

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <title>{talent.first_name ? `${talent.last_name} ${talent.first_name} | Talent Service` : 'Talent Service'}</title>

      <div className="mb-3 min-[992px]:hidden">
        <Button
          type="button"
          variant="ghost"
          color="neutral"
          size="small"
          onClick={() => navigate(backTo)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Button>
      </div>

      {/* ── Full profile hero remains in normal document flow ─────────────── */}
      <div ref={heroSummaryRef} className="bg-light-bg-secondary dark:bg-dark-bg-primary">
        <div className="rounded-xl border border-light-border-primary bg-light-bg-primary shadow-sm dark:border-dark-border-primary dark:bg-dark-bg-secondary">
          <div className="h-0.5 rounded-t-xl bg-gradient-to-r from-primary-500 via-purple-500 to-pink-400" />

          <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:px-5 lg:py-2.5">
            {/* Left: avatar + identity */}
            <div className="flex min-w-0 items-start gap-3 lg:items-center lg:gap-3.5">
              <div className="shrink-0 p-[2px] rounded-full bg-gradient-to-br from-primary-400 to-purple-500 shadow-sm">
                <Avatar
                  avatar={talent.authUser?.avatar}
                  name={talent.authUser?.name}
                  email={talent.authUser?.email}
                  sizeClasses="h-10 w-10 lg:h-11 lg:w-11"
                  iconSizeClasses="h-4 w-4 lg:h-5 lg:w-5"
                  icon={TypeIcon}
                  showInitialOnMissing={false}
                  iconBgClassName="bg-light-bg-secondary dark:bg-dark-bg-tertiary"
                  iconClassName="text-primary-600 dark:text-primary-400"
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-start gap-2 lg:items-center">
                  <h1 className="line-clamp-2 min-w-0 flex-1 break-words text-lg font-bold leading-tight text-light-text-primary dark:text-dark-text-primary lg:block lg:truncate lg:leading-normal">
                    {talent.last_name} {talent.first_name}
                  </h1>
                  {talent.id && (
                    <span className="hidden shrink-0 items-center gap-1 rounded-md border border-light-border-primary bg-light-bg-tertiary px-2 py-0.5 font-mono text-xs font-semibold text-light-text-secondary dark:border-dark-border-primary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary lg:inline-flex">
                      <span className="font-sans font-medium text-light-text-tertiary dark:text-dark-text-tertiary not-mono text-[10px] uppercase tracking-wide">id</span>
                      {talent.id}
                    </span>
                  )}
                  {talent.talent_id && (
                    <button
                      onClick={() => navigator.clipboard.writeText(talent.talent_id).then(() => toast.success('Copied!'))}
                      className="hidden shrink-0 rounded-md px-2 py-0.5 font-mono text-xs font-semibold text-white transition-opacity hover:opacity-80 lg:block"
                      style={{ backgroundColor: getTalentTypeHexColor(talent.talent_type) }}
                      title="Copy talent ID"
                    >
                      {talent.talent_id}
                    </button>
                  )}
                </div>

                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 lg:hidden">
                  {talent.id && (
                    <span className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-md border border-light-border-primary bg-light-bg-tertiary px-2 py-0.5 font-mono text-xs font-semibold text-light-text-secondary dark:border-dark-border-primary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary">
                      <span className="shrink-0 font-sans text-[10px] font-medium uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary">id</span>
                      <span className="truncate">{talent.id}</span>
                    </span>
                  )}
                  {talent.talent_id && (
                    <button
                      onClick={() => navigator.clipboard.writeText(talent.talent_id).then(() => toast.success('Copied!'))}
                      className="max-w-full min-w-0 truncate rounded-md px-2 py-0.5 font-mono text-xs font-semibold text-white transition-opacity hover:opacity-80"
                      style={{ backgroundColor: getTalentTypeHexColor(talent.talent_type) }}
                      title="Copy talent ID"
                    >
                      {talent.talent_id}
                    </button>
                  )}
                </div>

                <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2 lg:mt-0.5">
                  {/* Тип и его кнопка — одна группа: при общем gap-2 кнопка
                      висела ровно между типом и статусом и читалась как общая */}
                  <span className="inline-flex items-center gap-0.5 shrink-0">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md text-white shrink-0"
                      style={{ backgroundColor: getTalentTypeHexColor(talent.talent_type) }}
                    >
                      <TypeIcon className="h-3 w-3" />
                      {getTalentTypeLabel(talent.talent_type)}
                    </span>
                    {hasPermission('talent-service', talent.talent_type, 'update') && (
                      <button
                        onClick={() => setTypeChangeOpen(true)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary transition-colors hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 lg:h-auto lg:w-auto lg:p-0.5"
                        title="Change talent type"
                      >
                        <ArrowRightLeft className="h-3 w-3" />
                      </button>
                    )}
                  </span>

                  {talent.currentStatus && !isEditingStatus && (
                    <>
                      <span
                        className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold text-white"
                        style={{ backgroundColor: talent.currentStatus.color || '#9ca3af' }}
                        title="Status — what's going on with the talent right now"
                      >
                        <span className="font-medium text-white/70 text-[10px] uppercase tracking-wide">Status</span>
                        <span className="truncate">{talent.currentStatus.name || 'Not defined'}</span>
                      </span>
                      {hasPermission('talent-service', talent.talent_type, 'update') && (
                        <button
                          onClick={handleEditStatus}
                          className="inline-flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary transition-colors hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 lg:h-auto lg:w-auto lg:p-0.5"
                          title="Change status"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                      )}
                    </>
                  )}
                  {isEditingStatus && (
                    <div className="flex w-full min-w-0 basis-full items-center gap-2 lg:w-auto lg:basis-auto">
                      <div className="w-full min-w-0 lg:w-52">
                        <Autocomplete
                          fullWidth
                          options={statusOptions}
                          value={selectedStatusId}
                          onChange={(value) => {
                            setSelectedStatusId(value)
                            const toStatus = statusOptions.find(s => s.value === value)
                            setPendingStatus(toStatus)
                            setStatusTransitionOpen(true)
                          }}
                          placeholder="Select status"
                        />
                      </div>
                      <button onClick={() => setIsEditingStatus(false)} className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-red-500 transition-colors hover:text-red-600 lg:h-auto lg:w-auto" title="Cancel">
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {talent.currentStage && !isEditingStage && (
                    <>
                      <span
                        className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold text-white"
                        style={{ backgroundColor: talent.currentStage.color || '#9ca3af' }}
                        title="Stage — where the talent is in the pipeline"
                      >
                        <span className="font-medium text-white/70 text-[10px] uppercase tracking-wide">Stage</span>
                        <span className="truncate">{talent.currentStage.name || 'Not defined'}</span>
                      </span>
                      {hasPermission('talent-service', talent.talent_type, 'update') && (
                        <button
                          onClick={handleEditStage}
                          className="inline-flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary transition-colors hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 lg:h-auto lg:w-auto lg:p-0.5"
                          title="Change stage"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                      )}
                    </>
                  )}
                  {isEditingStage && (
                    <div className="flex w-full min-w-0 basis-full items-center gap-2 lg:w-auto lg:basis-auto">
                      <div className="w-full min-w-0 lg:w-52">
                        <Autocomplete
                          fullWidth
                          options={stageOptions}
                          value={selectedStageId}
                          onChange={(value) => {
                            setSelectedStageId(value)
                            const toStage = stageOptions.find(s => s.value === value)
                            setPendingStage(toStage)
                            setStageTransitionOpen(true)
                          }}
                          placeholder="Select stage"
                        />
                      </div>
                      <button onClick={() => setIsEditingStage(false)} className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-red-500 transition-colors hover:text-red-600 lg:h-auto lg:w-auto" title="Cancel">
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Edit / Delete */}
            <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
              {/* Отмечать в избранное может любой — права не проверяем */}
              <FavoriteStar talentId={talent.id} variant="button" className="[&_button]:min-h-10 lg:[&_button]:min-h-0" />
              {hasPermission('talent-service', talent.talent_type, 'update') && (
                <Link
                  to={`/talents/${id}/edit`}
                  className="inline-flex min-h-10 items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors lg:min-h-0"
                >
                  <Edit className="h-4 w-4 mr-1.5" />
                  Edit
                </Link>
              )}
              {hasPermission('talent-service', talent.talent_type, 'delete') && (
                <button
                  onClick={() => setShowMergeModal(true)}
                  className="hidden items-center px-3 py-1.5 border border-amber-300 dark:border-amber-700 text-sm font-medium rounded-lg text-amber-700 dark:text-amber-400 bg-light-bg-primary dark:bg-dark-bg-tertiary hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors lg:inline-flex"
                  title="Merge a duplicate into this profile"
                >
                  <Merge className="h-4 w-4 mr-1.5" />
                  Merge
                </button>
              )}
              {hasPermission('talent-service', talent.talent_type, 'delete') && (
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="hidden items-center px-3 py-1.5 border border-red-300 dark:border-red-700 text-sm font-medium rounded-lg text-red-700 dark:text-red-400 bg-light-bg-primary dark:bg-dark-bg-tertiary hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors lg:inline-flex"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Delete
                </button>
              )}
              {hasPermission('talent-service', talent.talent_type, 'delete') && (
                <SecondaryActionsMenu
                  onMerge={() => setShowMergeModal(true)}
                  onDelete={() => setDeleteConfirmOpen(true)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main layout ───────────────────────────────────────────────────── */}
      <div className="flex gap-6 py-4 pb-16 relative animate-fade-in">
        <main className="flex-1 min-w-0 space-y-4">

          {/* Mobile sticky identity and section navigation stack */}
          <div className="sticky top-0 z-30 -mx-1 bg-light-bg-secondary dark:bg-dark-bg-primary min-[992px]:hidden">
            {showCompactSummary && (
              <MobileTalentStickyContext
                talent={talent}
                canEdit={hasPermission('talent-service', talent.talent_type, 'update')}
              />
            )}
            <div className="overflow-x-auto px-1 py-2 scrollbar-hide">
              <div className="flex min-w-max gap-1.5">
                {visibleSections.map(section => {
                  const Icon = section.icon
                  const isActive = activeSection === section.id
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border
                        ${isActive
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-800'
                          : 'text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-primary border-light-border-secondary dark:border-dark-border-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary'
                        }`}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span>{section.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* ── Sections ──────────────────────────────────────────────────── */}
          <OverviewSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          {visibleSections.some(s => s.id === 'contacts') && (
            <ContactsSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'professions') && (
            <ProfessionsSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'tools') && (
            <ToolsSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'responsibilities') && (
            <ResponsibilitiesSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'languages') && (
            <LanguagesSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'cvs') && (
            <CVsSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}

          {visibleSections.some(s => s.id === 'media') && (
            <MediaSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'notes') && (
            <LazySection id="notes">
              <NotesSection talentId={id} talentType={talent.talent_type} hasPermission={hasPermission} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'salary') && (
            <SalarySection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}
          {visibleSections.some(s => s.id === 'important-dates') && (
            <LazySection id="important-dates">
              <ImportantDatesSection talentId={id} talentType={talent.talent_type} hasPermission={hasPermission} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'stages') && (
            <LazySection id="stages">
              <StagesSection talentId={id} talentType={talent.talent_type} hasPermission={hasPermission} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'communications') && (
            <LazySection id="communications">
              <CommunicationsSection talentId={id} talentType={talent.talent_type} hasPermission={hasPermission} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'ai-settings') && (
            <LazySection id="ai-settings">
              <AISettingsSection talentId={id} talentType={talent.talent_type} hasPermission={hasPermission} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'posts') && (
            <LazySection id="posts">
              <PostsSection talentId={id} hasPermission={hasPermission} />
            </LazySection>
          )}

          {visibleSections.some(s => s.id === 'courses') && (
            <LazySection id="courses">
              <CoursesSection authUserId={talent.auth_user_id} getToken={getToken} />
            </LazySection>
          )}

          {visibleSections.some(s => s.id === 'tasks') && (
            <LazySection id="tasks">
              <TasksSection authUserId={talent.auth_user_id} />
            </LazySection>
          )}

          {visibleSections.some(s => s.id === 'accounts') && (
            <LazySection id="accounts">
              <AccountsSection talent={talent} hasPermission={hasPermission} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'history') && (
            <LazySection id="history">
              <HistorySection talentId={id} />
            </LazySection>
          )}
          {visibleSections.some(s => s.id === 'public-profile') && (
            <PublicProfileSection talent={talent} talentId={id} hasPermission={hasPermission} setTalent={setTalent} />
          )}

        </main>
      </div>

      {/* ── Merge duplicate modal ────────────────────────────────────────── */}
      {showMergeModal && (
        <MergeTalentModal
          talent={talent}
          onClose={() => setShowMergeModal(false)}
          onMerged={(report, source) => {
            setShowMergeModal(false)
            toast.success(`Merged ${[source.first_name, source.last_name].filter(Boolean).join(' ')} into this profile`)
            fetchTalent()
          }}
        />
      )}

      {/* ── Talent type change modal ─────────────────────────────────────── */}
      {/* Монтируем только на время показа: иначе выбранный тип и причина
          остаются в стейте и всплывают при следующем открытии */}
      {typeChangeOpen && (
        <TalentTypeChangeModal
          isOpen={typeChangeOpen}
          onClose={() => setTypeChangeOpen(false)}
          talent={talent}
          onSuccess={(newType) => {
            setTypeChangeOpen(false)
            toast.success(`Talent moved to ${getTalentTypeLabel(newType)}`)
            fetchTalent()
          }}
        />
      )}

      {/* ── Status transition modal ──────────────────────────────────────── */}
      {statusTransitionOpen && pendingStatus && (
        <StatusTransitionModal
          isOpen={statusTransitionOpen}
          onClose={() => { setStatusTransitionOpen(false); setIsEditingStatus(false) }}
          entityType="talent"
          entityId={talent.id}
          entityName={`${talent.first_name} ${talent.last_name}`}
          fromStatus={talent.currentStatus}
          toStatus={pendingStatus}
          onSuccess={handleStatusTransitionSuccess}
        />
      )}

      {/* ── Stage transition modal ───────────────────────────────────────── */}
      {stageTransitionOpen && pendingStage && (
        <StageTransitionModal
          isOpen={stageTransitionOpen}
          onClose={() => { setStageTransitionOpen(false); setIsEditingStage(false) }}
          talentId={talent.id}
          talentName={`${talent.first_name} ${talent.last_name}`}
          fromStage={talent.currentStage}
          toStage={pendingStage}
          onSuccess={handleStageTransitionSuccess}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Talent"
        message={<>Are you sure you want to delete <strong>{talent.first_name} {talent.last_name}</strong>?</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deleting}
      />
    </>
  )
}

export default TalentDetail
